import { useCallback, useEffect, useState } from 'react';

import { Logger } from '../../Logger';

export interface AsyncDataResult<T> {
  isFetching: boolean;
  data: T;
  error?: Error;
  refresh: () => Promise<void>;
}

export interface UseAsyncDataProps<T> {
  fallback?: T;
  fetcher: () => Promise<T>;
}

export const useAsyncData = <T>({ fallback, fetcher }: UseAsyncDataProps<T>): AsyncDataResult<T> => {
  const [isFetching, setIsFetching] = useState(true);
  const [data, setData] = useState<T>(fallback as T);
  const [error, setError] = useState<Error | undefined>();

  const refresh = useCallback(async () => {
    try {
      setIsFetching(true);

      const data = await fetcher();

      setData(data);
      setError(undefined);
      setIsFetching(false);
    } catch (err) {
      setError(err as Error);
      setIsFetching(false);

      Logger.error(`Contentful fetch error: ${(err as Error).message}`, err as Error);
    }
  }, [fetcher]);

  // will cause refresh to happen on mount or whenever the fetcher changes
  useEffect(() => {
    refresh();
  }, [refresh]);

  return { isFetching, data, error, refresh };
};
